import { useEffect, useState, useCallback } from "react";
import {
  Globe, Loader2, Copy, Check, AlertTriangle, ShieldCheck,
  ShieldAlert, Trash2, Sparkles, Plus, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * CustomDomainPanel — dashboard section on /my-qr-codes for pointing
 * qrs.theirbrand.com at Geneo AI.
 *
 * Flow:
 *   1. Add domain (per-user unique, one at a time).
 *   2. Show CNAME instructions.
 *   3. Click Verify — backend does live DNS CNAME lookup.
 *   4. On success, SSL flips to `provisioning`. The prod LE worker will flip
 *      to `active` (or, in preview, an admin can). Once `active`, dynamic-QR
 *      short URLs auto-render on the custom domain.
 *   5. Delete anytime.
 *
 * Fallback: while unverified or SSL not yet active, short URLs stay on
 * afrogeneo.com/q/{code} — see backend `_short_url_base`.
 */
export function CustomDomainPanel({ subscriptionActive, onDomainChange }) {
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(null);
  const [cnameCopied, setCnameCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/qr/custom-domain");
      setDoc(data.domain === null ? null : data);
    } catch {
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addDomain = async () => {
    const domain = input.trim().toLowerCase();
    if (!domain) { toast.error("Enter a domain."); return; }
    setBusy("add");
    try {
      const { data } = await api.post("/qr/custom-domain", { domain });
      setDoc(data);
      setInput("");
      toast.success("Domain added — now update your DNS.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Failed to add domain."));
    } finally {
      setBusy(null);
    }
  };

  const verify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.post("/qr/custom-domain/verify");
      setDoc(data);
      if (data.ok || data.verify_state === "verified") {
        toast.success("CNAME verified — SSL provisioning kicked off.");
        onDomainChange?.();
      } else {
        toast.error(data.error || "Verification failed. Check your DNS and try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this custom domain? New QRs will fall back to afrogeneo.com/q/. Existing QRs already printed on your domain will stop resolving.")) return;
    setBusy("remove");
    try {
      await api.delete("/qr/custom-domain");
      setDoc(null);
      toast.success("Custom domain removed.");
      onDomainChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove.");
    } finally {
      setBusy(null);
    }
  };

  const copyCname = async () => {
    if (!doc?.cname_target) return;
    try {
      await navigator.clipboard.writeText(doc.cname_target);
      setCnameCopied(true);
      toast.success("CNAME target copied");
      setTimeout(() => setCnameCopied(false), 2000);
    } catch {}
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-[#F2C94C] animate-spin" />
      </div>
    );
  }

  // ── Locked: not on active subscription ───────────────────────────
  if (!subscriptionActive) {
    return (
      <div
        data-testid="cd-locked"
        className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6"
      >
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5 text-white/50" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-white/70 mb-1">Custom Domain</div>
            <p className="font-body text-sm text-white/50">
              Available to active Dynamic QR subscribers only. Resubscribe to unlock.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── No domain yet: add form ──────────────────────────────────────
  if (!doc || doc.domain == null) {
    return (
      <div
        data-testid="cd-empty"
        className="rounded-2xl border border-[#00E5FF]/25 bg-white/[0.02] p-5 sm:p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #00E5FF 0%, #7c3aed 100%)" }}
          >
            <Globe className="w-5 h-5 text-[#0A0812]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl mb-1">Point your own domain</div>
            <p className="font-body text-sm text-white/70 leading-relaxed">
              Add a hostname like <span className="text-[#00E5FF] font-mono">qrs.yourbrand.com</span> and every new dynamic QR you mint will encode <span className="text-[#F2C94C]">https://your-domain/q/…</span> instead of afrogeneo.com. One domain per account.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="qrs.yourbrand.com"
            data-testid="cd-input"
            onKeyDown={(e) => { if (e.key === "Enter") addDomain(); }}
            className="flex-1 px-4 py-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/70 focus:border-[#00E5FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/25 font-mono text-[13px] text-white placeholder:text-white/40"
          />
          <button
            onClick={addDomain}
            disabled={busy === "add"}
            data-testid="cd-add-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            ADD DOMAIN
          </button>
        </div>
      </div>
    );
  }

  // ── Domain exists → show state ────────────────────────────────────
  const verifyState = doc.verify_state;
  const sslState = doc.ssl_state;
  const isActive = verifyState === "verified" && sslState === "active";

  return (
    <div data-testid="cd-panel" className="space-y-4">
      {/* Pending-verification banner — shown until fully live */}
      {!isActive && (
        <div
          data-testid="cd-pending-banner"
          className={`rounded-2xl border p-4 flex items-start gap-3 ${
            verifyState === "failed"
              ? "border-[#EB5757]/40 bg-[#EB5757]/[0.08]"
              : "border-[#F2C94C]/40 bg-[#F2C94C]/[0.06]"
          }`}
        >
          {verifyState === "failed" ? (
            <ShieldAlert className="w-5 h-5 text-[#EB5757] flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-[#F2C94C] flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display text-base mb-0.5">
              {verifyState === "verified"
                ? "Domain verified — provisioning SSL"
                : verifyState === "failed"
                ? "Verification failed"
                : "Awaiting DNS verification"}
            </div>
            <p className="font-body text-sm text-white/80 leading-relaxed">
              {verifyState === "verified"
                ? "SSL certificate is being issued via Let's Encrypt. This usually takes a few minutes. Meanwhile, new QRs still fall back to afrogeneo.com/q/."
                : verifyState === "failed"
                ? doc.error || "Couldn't find the expected CNAME record. Double-check your DNS setup and re-verify."
                : "New QRs fall back to afrogeneo.com/q/ until this domain is verified."}
            </p>
          </div>
        </div>
      )}

      <div
        className="rounded-2xl border border-[#00E5FF]/25 bg-white/[0.02] p-5 sm:p-6"
        style={isActive ? {
          background: "radial-gradient(80% 100% at 100% 0%, rgba(39,174,96,0.10), transparent 60%), rgba(255,255,255,0.02)",
          borderColor: "rgba(39,174,96,0.35)",
        } : undefined}
      >
        {/* Header row */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: isActive
                ? "linear-gradient(135deg, #27AE60 0%, #00E5FF 100%)"
                : "linear-gradient(135deg, #00E5FF 0%, #7c3aed 100%)",
            }}
          >
            {isActive ? <ShieldCheck className="w-5 h-5 text-[#0A0812]" /> : <Globe className="w-5 h-5 text-[#0A0812]" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl truncate" data-testid="cd-domain">{doc.domain}</div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <StatePill label="DNS" state={verifyState} testid="cd-verify-state" />
              <StatePill label="SSL" state={sslState} testid="cd-ssl-state" />
              {isActive && (
                <span className="font-mono-hy text-[9px] tracking-[0.18em] text-[#27AE60] flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> LIVE ON YOUR DOMAIN
                </span>
              )}
            </div>
          </div>
          <button
            onClick={remove}
            disabled={busy === "remove"}
            data-testid="cd-remove-btn"
            aria-label="Remove custom domain"
            className="w-9 h-9 rounded-xl border border-white/[0.08] hover:border-[#EB5757] hover:bg-[#EB5757]/[0.08] hover:text-[#EB5757] transition-all flex items-center justify-center disabled:opacity-40"
          >
            {busy === "remove" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* CNAME setup instructions — shown until active */}
        {!isActive && (
          <div className="rounded-xl border border-white/[0.08] bg-[#0A0A0A]/60 p-4 mb-4" data-testid="cd-instructions">
            <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.24em] mb-3">
              1 · ADD THIS CNAME RECORD AT YOUR DNS PROVIDER
            </div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="font-mono-hy text-[9px] text-white/50 tracking-widest mb-1">TYPE</div>
                <div className="font-mono text-[13px] text-white bg-white/[0.03] rounded px-2 py-1.5 border border-white/[0.06] w-fit">CNAME</div>
              </div>
              <div>
                <div className="font-mono-hy text-[9px] text-white/50 tracking-widest mb-1">HOST / NAME</div>
                <div className="font-mono text-[13px] text-white bg-white/[0.03] rounded px-2 py-1.5 border border-white/[0.06] truncate">
                  {doc.domain}
                </div>
              </div>
              <div>
                <div className="font-mono-hy text-[9px] text-white/50 tracking-widest mb-1">POINTS TO</div>
                <button
                  onClick={copyCname}
                  data-testid="cd-copy-cname"
                  className="w-full font-mono text-[13px] text-white bg-white/[0.03] hover:bg-white/[0.06] rounded px-2 py-1.5 border border-[#00E5FF]/30 hover:border-[#00E5FF] flex items-center justify-between gap-2 transition-all"
                  title="Copy CNAME target"
                >
                  <span className="truncate">{doc.cname_target}</span>
                  {cnameCopied ? <Check className="w-3.5 h-3.5 text-[#27AE60] flex-shrink-0" /> : <Copy className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />}
                </button>
              </div>
            </div>
            <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.24em] mt-5 mb-3">
              2 · VERIFY
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={verify}
                disabled={busy === "verify"}
                data-testid="cd-verify-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {busy === "verify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {verifyState === "verified" ? "RE-CHECK DNS" : "VERIFY CNAME"}
              </button>
              <p className="font-mono-hy text-[9px] text-white/60 tracking-widest self-center">
                DNS PROPAGATION CAN TAKE A FEW MINUTES
              </p>
            </div>
          </div>
        )}

        {/* Live preview */}
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 flex items-center gap-3">
          <span className="font-mono-hy text-[9px] text-white/50 tracking-widest">
            {isActive ? "NEW QRS ENCODE →" : "PREVIEW →"}
          </span>
          <span className="font-mono text-[12px] text-white/80 truncate flex-1" title={doc.short_url_preview}>
            {doc.short_url_preview}
          </span>
          {isActive && (
            <span className="font-mono-hy text-[9px] text-[#27AE60] tracking-widest flex-shrink-0">
              ACTIVE
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── State pill ──────────────────────────────────────────────────── */
function StatePill({ label, state, testid }) {
  const map = {
    pending:     { c: "#F2C94C", t: "PENDING" },
    provisioning:{ c: "#00E5FF", t: "PROVISIONING" },
    verified:    { c: "#27AE60", t: "VERIFIED" },
    active:      { c: "#27AE60", t: "ACTIVE" },
    failed:      { c: "#EB5757", t: "FAILED" },
  };
  const s = map[state] || map.pending;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono-hy text-[9px] tracking-[0.18em] border"
      style={{ color: s.c, borderColor: `${s.c}55`, backgroundColor: `${s.c}12` }}
    >
      {label}: {s.t}
    </span>
  );
}
